import { useMemo } from 'react';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useTranslation } from '@ibk/shared-translation';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { formatCurrency, formatShortDateWithoutTimezone, getAccountName, getCurrentDateISO, isValidCpf, } from '@ibk/shared-utils';
import { BankName } from '../../enums';
export function usePixTransferRevisionData(data) {
    const { t } = useTranslation();
    const { maskDocumentHidden, isValidCnpj } = useCnpjAlphanumeric();
    const originInfo = useMemo(() => {
        const accountInfo = getSelectedAccountInfo();
        const name = getAccountName(accountInfo) || t('not-available');
        let taxIdentifier = t('not-available');
        let taxIdentifierLabel = t('cpf-cnpj').toUpperCase();
        if (accountInfo?.documentNumber) {
            const isCpf = isValidCpf(accountInfo.documentNumber);
            taxIdentifier = maskDocumentHidden(accountInfo.documentNumber) ?? t('not-available');
            taxIdentifierLabel = isCpf ? t('cpf').toUpperCase() : t('cnpj').toUpperCase();
        }
        return {
            name,
            taxIdentifier,
            taxIdentifierLabel,
            bank: BankName.CELCOIN,
        };
    }, [t, maskDocumentHidden]);
    const destinationInfo = useMemo(() => {
        if (!data.pixKeyDetails && !data.pixAgencyAccount) {
            return {
                name: t('receipient-name-not-available'),
                key: data.keyValue || t('not-available'),
                taxIdentifier: t('not-available'),
                taxIdentifierLabel: t('cpf-cnpj').toUpperCase(),
                bank: t('bank-validation-required'),
            };
        }
        const getDocumentTypeAndLabel = (document) => {
            const isCpf = isValidCpf(document);
            const isCnpj = isValidCnpj(document);
            let label = t('cpf-cnpj').toUpperCase();
            if (isCpf) {
                label = t('cpf').toUpperCase();
            }
            else if (isCnpj) {
                label = t('cnpj').toUpperCase();
            }
            return {
                label,
                maskedValue: maskDocumentHidden(document) ?? t('not-available'),
            };
        };
        const { label, maskedValue } = getDocumentTypeAndLabel(data.pixKeyDetails?.taxIdentifier ?? data.pixAgencyAccount?.document ?? t('not-available'));
        return {
            name: data.pixKeyDetails?.name ?? data.pixAgencyAccount?.name ?? t('receipient-name-not-available'),
            key: data.keyValue ?? t('not-available'),
            taxIdentifier: maskedValue,
            taxIdentifierLabel: label,
            bank: data.pixKeyDetails?.bank ?? data.pixAgencyAccount?.bankName ?? t('bank-validation-required'),
        };
    }, [data.pixKeyDetails, data.pixAgencyAccount, data.keyValue, t, maskDocumentHidden, isValidCnpj]);
    const formattedValue = useMemo(() => {
        return formatCurrency(data.value, 'BRL');
    }, [data.value]);
    const formattedPaymentDate = useMemo(() => {
        if (!data.paymentDate) {
            const todayISO = getCurrentDateISO();
            return formatShortDateWithoutTimezone(todayISO);
        }
        return formatShortDateWithoutTimezone(data.paymentDate);
    }, [data.paymentDate]);
    const isLoading = useMemo(() => {
        return !data.pixKeyDetails && !!data.keyValue;
    }, [data.pixKeyDetails, data.keyValue]);
    return {
        originInfo,
        destinationInfo,
        formattedValue,
        formattedPaymentDate,
        isLoading,
    };
}
